/**
 * nav-model — modelo DECLARATIVO de la navegación lateral por rol/capacidad.
 *
 * Cada item define `visible(ctx)` sobre un `NavContext` puro (sin hooks), así
 * la matriz de visibilidad es testeable en vitest sin DOM. El contexto se
 * construye reactivamente en `use-nav-context.ts` (sesión per-tab + rol de la
 * org activa + guardian/teacher local-first), de modo que un switch de cuenta u
 * org re-evalúa la visibilidad al instante.
 *
 * SOLO UX: la autoridad real siguen siendo los guards de ruta y el backend
 * (write-path / middleware admin). Ocultar un item no es seguridad; evitarle al
 * usuario un 403/redirect sí es cortesía.
 */
import type { Component } from "solid-js";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import {
  Banknote,
  BarChart3,
  BookMarked,
  BookOpen,
  Building2,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Contact,
  CreditCard,
  FileSpreadsheet,
  GraduationCap,
  History,
  IdCard,
  LifeBuoy,
  Megaphone,
  Route as RouteIcon,
  School,
  ShieldCheck,
  Sparkles,
  Trash2,
  Trophy,
  User,
  Users,
  UsersRound,
} from "lucide-solid";
import { isAdminCapable, MODALITY_IN_PERSON_KEY, type OrgRole, roleCan } from "@space/shared";

/** Contexto puro de visibilidad (lo arma `useNavContext` reactivamente). */
export interface NavContext {
  /** Cuenta REAL per-tab (un invitado explícito nunca está "logged"). */
  isLoggedIn: boolean;
  /** Rol de plataforma (`session.user.role`) — capacidades F2-C. */
  platformRole: string | null;
  /** Rol normalizado en la org ACTIVA (null cuando no hay org/membresía). */
  orgRole: OrgRole | null;
  /** Tiene filas cohort_teacher propias (o rol teacher en la org activa). */
  isTeacher: boolean;
  /** Tiene filas guardian_link propias sincronizadas (es representante). */
  isGuardian: boolean;
  /**
   * FLAGS: ¿está la feature efectiva-habilitada (grafo incluido) para la org
   * activa? La arma `useNavContext` con `usePlatformFlags` + org_settings
   * sincronizada; en tests basta `() => true`.
   */
  featureEnabled: (key: string) => boolean;
}

export interface NavItem {
  to: string;
  label: MessageDescriptor;
  icon: Component<{ class?: string }>;
  testid: string;
  visible: (ctx: NavContext) => boolean;
  /**
   * FLAGS: feature que gatea el item (además del rol). Deshabilitada para la
   * org activa ⇒ el item desaparece de la barra (la ruta muestra su estado
   * «deshabilitada por tu academia» si se navega directo).
   */
  feature?: string;
}

export interface NavSection {
  id: "main" | "academy" | "management" | "account";
  /** Cabecera de sección (null = sin cabecera, sección principal). */
  header: MessageDescriptor | null;
  items: NavItem[];
}

// ── escalones de rol de org (de menor a mayor privilegio operativo) ──────────
const TEACHER_TIER: readonly OrgRole[] = ["teacher", "staff", "campus_admin", "org_admin", "owner"];
const STAFF_TIER: readonly OrgRole[] = ["staff", "campus_admin", "org_admin", "owner"];
const CAMPUS_TIER: readonly OrgRole[] = ["campus_admin", "org_admin", "owner"];
const ORG_ADMIN_TIER: readonly OrgRole[] = ["org_admin", "owner"];
const FINANCE_ORG_ROLES: readonly OrgRole[] = ["staff", "org_admin", "owner"];

const inTier = (ctx: NavContext, tier: readonly OrgRole[]) =>
  ctx.orgRole !== null && tier.includes(ctx.orgRole);

/** Escalón docente: filas cohort_teacher propias también cuentan (F3-D). */
const teacherTier = (ctx: NavContext) => ctx.isTeacher || inTier(ctx, TEACHER_TIER);

const logged = (ctx: NavContext) => ctx.isLoggedIn;
const everyone = () => true;

export const NAV_SECTIONS: NavSection[] = [
  {
    id: "main",
    header: null,
    items: [
      {
        to: "/learn",
        label: msg`Learn`,
        icon: GraduationCap,
        testid: "nav-learn",
        visible: everyone,
      },
      {
        to: "/catalog",
        label: msg`Catalog`,
        icon: BookOpen,
        testid: "nav-catalog",
        visible: everyone,
      },
      {
        to: "/schedule",
        label: msg`My schedule`,
        icon: CalendarDays,
        testid: "nav-schedule",
        visible: logged,
      },
      {
        to: "/assignments",
        label: msg`My assignments`,
        icon: ClipboardList,
        testid: "nav-assignments",
        visible: logged,
      },
      // 1C · A4: la casa del ALUMNO. `/academy/cohorts` es del escalón docente,
      // así que sin esta entrada quien estudia no tenía puerta a su aula.
      {
        to: "/classroom",
        label: msg`My classrooms`,
        icon: School,
        testid: "nav-classroom",
        visible: logged,
      },
      // B6 · NIVELES: la puerta del alumno a su camino. Gateada por el flag del
      // camino visual (no por el paraguas): apagarla devuelve /learn a la lista
      // de siempre sin quitarle al alumno nada de lo que ya tenía.
      {
        to: "/path",
        label: msg`My path`,
        icon: RouteIcon,
        testid: "nav-path",
        feature: "academy.progression.path",
        visible: logged,
      },
      {
        to: "/final-project",
        label: msg`Final project`,
        icon: Trophy,
        testid: "nav-final-project",
        feature: "academy.final_project",
        visible: logged,
      },
      {
        to: "/payments",
        label: msg`My payments`,
        icon: CreditCard,
        testid: "nav-payments",
        feature: "billing.core",
        visible: logged,
      },
      {
        to: "/ai-tutor",
        label: msg`AI Tutor`,
        icon: Sparkles,
        testid: "nav-ai-tutor",
        feature: "academy.ai_tutor",
        visible: logged,
      },
      {
        to: "/leaderboard",
        label: msg`Leagues`,
        icon: Trophy,
        testid: "nav-leaderboard",
        feature: "gamification.core",
        visible: everyone,
      },
    ],
  },
  {
    id: "academy",
    header: msg`Academy`,
    items: [
      {
        to: "/academy",
        label: msg`Dashboard`,
        icon: BarChart3,
        testid: "nav-academy",
        visible: teacherTier,
      },
      {
        to: "/academy/cohorts",
        label: msg`Cohorts`,
        icon: UsersRound,
        testid: "nav-cohorts",
        visible: teacherTier,
      },
      // M3: cola de corrección del tutor (y de cualquier revisor). Gateada por la
      // vertical de tutorías; visible para el escalón docente (incl. tutores, que
      // tienen filas cohort_teacher propias) y admins.
      {
        to: "/academy/tutoring",
        label: msg`Tutoring`,
        icon: ClipboardCheck,
        testid: "nav-tutoring",
        feature: "academy.tutoring",
        visible: teacherTier,
      },
      {
        to: "/academy/people",
        label: msg`People`,
        icon: Users,
        testid: "nav-people",
        visible: (ctx) => inTier(ctx, STAFF_TIER),
      },
      // §L1 · online-first: las sedes son infraestructura PRESENCIAL. La
      // vertical nace apagada, así que este item no se ve — pero ni la pantalla
      // ni sus datos desaparecen: vuelven en cuanto se encienda el flag.
      {
        to: "/academy/campuses",
        label: msg`Campuses`,
        icon: Building2,
        testid: "nav-campuses",
        feature: MODALITY_IN_PERSON_KEY,
        visible: (ctx) => inTier(ctx, CAMPUS_TIER),
      },
      {
        to: "/academy/programs",
        label: msg`Programs`,
        icon: RouteIcon,
        testid: "nav-programs",
        visible: (ctx) => inTier(ctx, ORG_ADMIN_TIER),
      },
      // B1 · titularidad: qué pasa si un alumno no ha pagado. Decidir eso —y a
      // quién se le perdona— es cosa del dueño o del administrador de la
      // academia, no del mostrador: por eso ORG_ADMIN_TIER y no STAFF_TIER.
      {
        to: "/academy/entitlement",
        label: msg`Entitlement`,
        icon: ShieldCheck,
        testid: "nav-entitlement",
        feature: "academy.entitlement",
        visible: (ctx) => inTier(ctx, ORG_ADMIN_TIER),
      },
      {
        to: "/academy/announcements",
        label: msg`Announcements`,
        icon: Megaphone,
        testid: "nav-announcements",
        visible: teacherTier,
      },
      // Regla vigente de autoría: cualquier cuenta real puede autorar sus
      // cursos (CREAR exige cuenta; el sync fuerza la authz de autor).
      {
        to: "/authoring",
        label: msg`Authoring`,
        icon: BookMarked,
        testid: "nav-authoring",
        visible: logged,
      },
    ],
  },
  {
    id: "management",
    header: msg`Management`,
    items: [
      {
        to: "/finance",
        label: msg`Cashier`,
        icon: Banknote,
        testid: "nav-finance",
        feature: "billing.core",
        visible: (ctx) =>
          roleCan(ctx.platformRole, "payments.manage") || inTier(ctx, FINANCE_ORG_ROLES),
      },
      {
        to: "/crm",
        label: msg`CRM`,
        icon: Contact,
        testid: "nav-crm",
        feature: "crm.leads",
        visible: (ctx) => roleCan(ctx.platformRole, "leads.manage") || inTier(ctx, STAFF_TIER),
      },
      {
        // Espejo del guard REAL: /admin/import-export vive bajo /admin
        // (isAdminCapable) y el hub lista entidades por capacidad — solo tiene
        // contenido para quien gestiona datos o leads.
        to: "/admin/import-export",
        label: msg`Import / Export`,
        icon: FileSpreadsheet,
        testid: "nav-import-export",
        visible: (ctx) =>
          isAdminCapable(ctx.platformRole) &&
          (roleCan(ctx.platformRole, "data.manage") || roleCan(ctx.platformRole, "leads.manage")),
      },
    ],
  },
  {
    id: "account",
    header: msg`Account`,
    items: [
      {
        to: "/activity",
        label: msg`Activity`,
        icon: History,
        testid: "nav-activity",
        visible: logged,
      },
      { to: "/trash", label: msg`Trash`, icon: Trash2, testid: "nav-trash", visible: logged },
      // 1D · E1: «Mi expediente» (avance, notas, asistencia, pagos y
      // certificados). Era una pantalla a la que solo llegaba un REPRESENTANTE
      // desde el nav, aunque su contenido es del propio alumno; el selector de
      // pupilos sigue viviendo dentro, así que un representante entra por aquí
      // igual que antes y ya no hacen falta dos entradas a la misma ruta.
      {
        to: "/profile/academic",
        label: msg`My record`,
        icon: Contact,
        testid: "nav-my-record",
        visible: logged,
      },
      { to: "/profile", label: msg`Profile`, icon: User, testid: "nav-profile", visible: everyone },
      // §M1 · «Mis perfiles»: qué soy en cada academia, qué me deja hacer y qué
      // no — y POR QUÉ (rol insuficiente vs. feature apagada). Solo con cuenta
      // real: un invitado no tiene membresías que explicar.
      {
        to: "/profiles",
        label: msg`My profiles`,
        icon: IdCard,
        testid: "nav-profiles",
        visible: logged,
      },
      // AUTONOMY: centro de ayuda in-app (guías por rol, /help) — visible para
      // todos: la auto-descripción del sistema no se esconde tras un login.
      {
        to: "/help",
        label: msg`Help`,
        icon: LifeBuoy,
        testid: "nav-help",
        visible: everyone,
      },
      // Única entrada al hub de back-office: mismo helper que el guard /admin.
      {
        to: "/admin",
        label: msg`Admin`,
        icon: ShieldCheck,
        testid: "nav-admin",
        visible: (ctx) => isAdminCapable(ctx.platformRole),
      },
    ],
  },
];

/** Secciones con al menos un item visible, ya filtradas para `ctx`. */
export function visibleSections(
  ctx: NavContext,
): { id: NavSection["id"]; header: MessageDescriptor | null; items: NavItem[] }[] {
  return NAV_SECTIONS.map((section) => ({
    id: section.id,
    header: section.header,
    items: section.items.filter(
      (item) =>
        item.visible(ctx) && (item.feature === undefined || ctx.featureEnabled(item.feature)),
    ),
  })).filter((section) => section.items.length > 0);
}
